/**
 * POIs Store - Pinia
 *
 * Manages POI entities, POI-photo blobs, and POI link tables.
 */

import { defineStore } from 'pinia';
import GeoMoverDB from '@/db/geoMoverDB';
import { appDebugLog } from '@/utils/appDebugLog';

const DEFAULT_POI_LIST_TITLE = 'Default POIs';
const DEFAULT_ROUTE_LINK_THRESHOLD_METERS = 100;
const VALID_POI_SOURCES = new Set(['map-click', 'search', 'import', 'reverse-geocode', 'manual']);

let photoCompressionHook = null;
let hasWarnedLegacyPoiNoteLinksFallback = false;

function normalizeRequiredTitle (value) {
  const next = String(value || '').trim();
  if (!next) throw new Error('POI title is required.');
  return next;
}

function normalizeOptionalString (value) {
  if (value === null || value === undefined) return null;
  const next = String(value).trim();
  return next || null;
}

function normalizeFiniteNumber (value, fallback = null) {
  if (value === null || value === undefined || value === '') return fallback;
  const next = Number(value);
  return Number.isFinite(next) ? next : fallback;
}

function normalizeNumericId (value, fallback = null) {
  if (value === null || value === undefined || value === '') return fallback;
  const next = Number(value);
  return Number.isFinite(next) ? next : fallback;
}

function normalizeIdArray (value) {
  if (!Array.isArray(value)) return [];

  const ids = value
    .map((item) => Number(item))
    .filter((item) => Number.isFinite(item));

  return [...new Set(ids)];
}

function normalizeSource (value, fallback = 'manual') {
  if (value === null || value === undefined) return fallback;

  const next = String(value).trim();
  if (!next) return fallback;

  if (!VALID_POI_SOURCES.has(next)) {
    throw new Error(`Invalid POI source: ${next}`);
  }

  return next;
}

function sortPois (rows) {
  return [...rows].sort((a, b) => {
    const aUpdated = Number(a?.updatedAt || 0);
    const bUpdated = Number(b?.updatedAt || 0);
    if (aUpdated !== bUpdated) return bUpdated - aUpdated;

    const aCreated = Number(a?.createdAt || 0);
    const bCreated = Number(b?.createdAt || 0);
    if (aCreated !== bCreated) return bCreated - aCreated;

    const aTitle = String(a?.title || '').toLowerCase();
    const bTitle = String(b?.title || '').toLowerCase();
    if (aTitle !== bTitle) return aTitle.localeCompare(bTitle);

    return Number(a?.id || 0) - Number(b?.id || 0);
  });
}

function sortLinks (rows) {
  return [...rows].sort((a, b) => Number(a?.id || 0) - Number(b?.id || 0));
}

function sortPhotos (rows) {
  return [...rows].sort((a, b) => Number(a?.createdAt || 0) - Number(b?.createdAt || 0) || Number(a?.id || 0) - Number(b?.id || 0));
}

function findById (rows, id) {
  const numericId = Number(id);
  if (!Number.isFinite(numericId)) return null;
  return rows.find((row) => Number(row?.id) === numericId) || null;
}

async function resolveDefaultPoiListId () {
  const poiLists = await GeoMoverDB.findAll('poiLists');
  const rows = Array.isArray(poiLists) ? poiLists : [];

  const seededDefault = rows.find((row) => Boolean(row?.isDefault));
  if (seededDefault && Number.isFinite(Number(seededDefault.id))) {
    return Number(seededDefault.id);
  }

  const fallbackPayload = {
    title: DEFAULT_POI_LIST_TITLE,
    isDefault: true,
    createdAt: Date.now(),
    updatedAt: null
  };

  const createdId = await GeoMoverDB.addOne('poiLists', fallbackPayload);
  return Number(createdId);
}

async function defaultPhotoCompression ({ blob }) {
  return { blob };
}

function getCompressionHook (overrideHook = null) {
  if (typeof overrideHook === 'function') return overrideHook;
  if (typeof photoCompressionHook === 'function') return photoCompressionHook;
  return defaultPhotoCompression;
}

function buildCreatePoiRecord (input, fallbackListId, options = {}) {
  const now = Date.now();
  const lat = normalizeFiniteNumber(input.lat, null);
  const lon = normalizeFiniteNumber(input.lon, null);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
    throw new Error('POI latitude and longitude are required.');
  }

  const listId = normalizeNumericId(input.listId, fallbackListId);
  if (!Number.isFinite(listId)) {
    throw new Error('POI listId is required.');
  }

  const version = Math.max(1, Number(input.version || 1));
  const versionUpdatedAt = Number.isFinite(Number(input.versionUpdatedAt))
    ? Number(input.versionUpdatedAt)
    : now;

  return {
    title: normalizeRequiredTitle(input.title),
    description: normalizeOptionalString(input.description),
    lat,
    lon,
    alt: normalizeFiniteNumber(input.alt, null),
    iconKey: normalizeOptionalString(input.iconKey),
    taxonomyIds: normalizeIdArray(input.taxonomyIds),
    listId,
    tagIds: normalizeIdArray(input.tagIds),
    avatarPhotoId: normalizeNumericId(input.avatarPhotoId, null),
    photoIds: normalizeIdArray(input.photoIds),
    searchProviderId: normalizeNumericId(input.searchProviderId, null),
    source: normalizeSource(input.source, options.sourceFallback || 'manual'),
    version,
    versionUpdatedAt,
    versionUpdatedByContext: normalizeOptionalString(input.versionUpdatedByContext)
      || normalizeOptionalString(options.versionContext)
      || 'poi:create',
    createdAt: Number.isFinite(Number(input.createdAt)) ? Number(input.createdAt) : now,
    updatedAt: Number.isFinite(Number(input.updatedAt)) ? Number(input.updatedAt) : null
  };
}

function buildUpdatedPoiRecord (current, updates, options = {}) {
  const now = Date.now();
  const next = {
    ...current,
    taxonomyIds: normalizeIdArray(current?.taxonomyIds),
    tagIds: normalizeIdArray(current?.tagIds),
    photoIds: normalizeIdArray(current?.photoIds)
  };

  if (updates.title !== undefined) {
    next.title = normalizeRequiredTitle(updates.title);
  }

  if (updates.description !== undefined) {
    next.description = normalizeOptionalString(updates.description);
  }

  if (updates.lat !== undefined) {
    const lat = normalizeFiniteNumber(updates.lat, null);
    if (!Number.isFinite(lat)) throw new Error('Valid POI latitude is required.');
    next.lat = lat;
  }

  if (updates.lon !== undefined) {
    const lon = normalizeFiniteNumber(updates.lon, null);
    if (!Number.isFinite(lon)) throw new Error('Valid POI longitude is required.');
    next.lon = lon;
  }

  if (updates.alt !== undefined) {
    next.alt = normalizeFiniteNumber(updates.alt, null);
  }

  if (updates.iconKey !== undefined) {
    next.iconKey = normalizeOptionalString(updates.iconKey);
  }

  if (updates.taxonomyIds !== undefined) {
    next.taxonomyIds = normalizeIdArray(updates.taxonomyIds);
  }

  if (updates.listId !== undefined) {
    const fallbackListId = Number.isFinite(Number(options.fallbackListId)) ? Number(options.fallbackListId) : null;
    const nextListId = normalizeNumericId(updates.listId, fallbackListId);
    if (!Number.isFinite(nextListId)) throw new Error('Valid POI listId is required.');
    next.listId = nextListId;
  }

  if (updates.tagIds !== undefined) {
    next.tagIds = normalizeIdArray(updates.tagIds);
  }

  if (updates.avatarPhotoId !== undefined) {
    next.avatarPhotoId = normalizeNumericId(updates.avatarPhotoId, null);
  }

  if (updates.photoIds !== undefined) {
    next.photoIds = normalizeIdArray(updates.photoIds);
  }

  if (updates.searchProviderId !== undefined) {
    next.searchProviderId = normalizeNumericId(updates.searchProviderId, null);
  }

  if (updates.source !== undefined) {
    next.source = normalizeSource(updates.source, current.source || 'manual');
  }

  next.updatedAt = now;

  if (options.bumpVersion !== false) {
    next.version = Math.max(1, Number(current?.version || 1) + 1);
    next.versionUpdatedAt = now;
    next.versionUpdatedByContext = normalizeOptionalString(options.versionContext) || 'poi:update';
  }

  return next;
}

function ensureBlob (photoInput) {
  if (photoInput instanceof Blob) return photoInput;
  if (photoInput?.blob instanceof Blob) return photoInput.blob;
  throw new Error('POI photo blob is required.');
}

function canInspectObjectStores () {
  return Boolean(GeoMoverDB?.db?.objectStoreNames && typeof GeoMoverDB.db.objectStoreNames.contains === 'function');
}

function hasObjectStore (storeName) {
  if (!canInspectObjectStores()) return true;
  return GeoMoverDB.db.objectStoreNames.contains(storeName);
}

function resolvePoiNoteLinksStoreName () {
  if (hasObjectStore('poiNoteLinks')) {
    return 'poiNoteLinks';
  }

  if (hasObjectStore('notePoiLinks')) {
    if (!hasWarnedLegacyPoiNoteLinksFallback) {
      appDebugLog.warn('PoisStore', 'Using legacy "notePoiLinks" IndexedDB store as fallback.');
      hasWarnedLegacyPoiNoteLinksFallback = true;
    }
    return 'notePoiLinks';
  }

  // Default to canonical name when store inspection is unavailable.
  return 'poiNoteLinks';
}

function hasAnyPoiNoteLinksStore () {
  if (!canInspectObjectStores()) return true;
  return hasObjectStore('poiNoteLinks') || hasObjectStore('notePoiLinks');
}

export const usePoisStore = defineStore('pois', {
  state: () => ({
    pois: [],
    poiNoteLinks: [],
    poiRouteLinks: [],
    poiPhotos: [],
    isPoisLoaded: false,
    isPoiNoteLinksLoaded: false,
    isPoiRouteLinksLoaded: false,
    isPoiPhotosLoaded: false
  }),

  getters: {
    getAllPois: (state) => state.pois,
    getPoiById: (state) => (poiId) => findById(state.pois, poiId),
    getPoisByListId: (state) => (listId) => {
      const numericListId = Number(listId);
      if (!Number.isFinite(numericListId)) return [];
      return state.pois.filter((row) => Number(row?.listId) === numericListId);
    },
    getPoisByTaxonomyId: (state) => (taxonomyId) => {
      const numericTaxonomyId = Number(taxonomyId);
      if (!Number.isFinite(numericTaxonomyId)) return [];
      return state.pois.filter((row) => normalizeIdArray(row?.taxonomyIds).includes(numericTaxonomyId));
    },
    getPoisByTagId: (state) => (tagId) => {
      const numericTagId = Number(tagId);
      if (!Number.isFinite(numericTagId)) return [];
      return state.pois.filter((row) => normalizeIdArray(row?.tagIds).includes(numericTagId));
    },
    getNoteIdsByPoiId: (state) => (poiId) => {
      const numericPoiId = Number(poiId);
      if (!Number.isFinite(numericPoiId)) return [];

      return state.poiNoteLinks
        .filter((row) => Number(row?.poiId) === numericPoiId)
        .map((row) => Number(row.noteId))
        .filter((row) => Number.isFinite(row));
    },
    getPoiIdsByNoteId: (state) => (noteId) => {
      const numericNoteId = Number(noteId);
      if (!Number.isFinite(numericNoteId)) return [];

      return state.poiNoteLinks
        .filter((row) => Number(row?.noteId) === numericNoteId)
        .map((row) => Number(row.poiId))
        .filter((row) => Number.isFinite(row));
    },
    getRouteLinksByPoiId: (state) => (poiId) => {
      const numericPoiId = Number(poiId);
      if (!Number.isFinite(numericPoiId)) return [];
      return state.poiRouteLinks.filter((row) => Number(row?.poiId) === numericPoiId);
    },
    getPhotosByPoiId: (state) => (poiId) => {
      const numericPoiId = Number(poiId);
      if (!Number.isFinite(numericPoiId)) return [];
      return sortPhotos(state.poiPhotos.filter((row) => Number(row?.poiId) === numericPoiId));
    }
  },

  actions: {
    async loadPois () {
      try {
        const rows = await GeoMoverDB.findAll('pois');
        this.pois = sortPois(Array.isArray(rows) ? rows : []);
        this.isPoisLoaded = true;
        return this.pois;
      }
      catch (error) {
        appDebugLog.error('PoisStore', 'Error loading POIs:', error);
        this.pois = [];
        this.isPoisLoaded = false;
        return [];
      }
    },

    async loadPoiNoteLinks () {
      try {
        if (!hasAnyPoiNoteLinksStore()) {
          appDebugLog.warn('PoisStore', 'No POI-note link store exists in IndexedDB. Continuing with empty link set.');
          this.poiNoteLinks = [];
          this.isPoiNoteLinksLoaded = true;
          return [];
        }

        const rows = await GeoMoverDB.findAll(resolvePoiNoteLinksStoreName());
        this.poiNoteLinks = sortLinks(Array.isArray(rows) ? rows : []);
        this.isPoiNoteLinksLoaded = true;
        return this.poiNoteLinks;
      }
      catch (error) {
        appDebugLog.error('PoisStore', 'Error loading poiNoteLinks:', error);
        this.poiNoteLinks = [];
        this.isPoiNoteLinksLoaded = false;
        return [];
      }
    },

    async loadPoiRouteLinks () {
      try {
        const rows = await GeoMoverDB.findAll('poiRouteLinks');
        this.poiRouteLinks = sortLinks(Array.isArray(rows) ? rows : []);
        this.isPoiRouteLinksLoaded = true;
        return this.poiRouteLinks;
      }
      catch (error) {
        appDebugLog.error('PoisStore', 'Error loading poiRouteLinks:', error);
        this.poiRouteLinks = [];
        this.isPoiRouteLinksLoaded = false;
        return [];
      }
    },

    async loadPoiPhotos () {
      try {
        const rows = await GeoMoverDB.findAll('poiPhotos');
        this.poiPhotos = sortPhotos(Array.isArray(rows) ? rows : []);
        this.isPoiPhotosLoaded = true;
        return this.poiPhotos;
      }
      catch (error) {
        appDebugLog.error('PoisStore', 'Error loading poiPhotos:', error);
        this.poiPhotos = [];
        this.isPoiPhotosLoaded = false;
        return [];
      }
    },

    async loadPoiDataGraph () {
      await Promise.all([
        this.loadPois(),
        this.loadPoiNoteLinks(),
        this.loadPoiRouteLinks(),
        this.loadPoiPhotos()
      ]);
    },

    setPhotoCompressionHook (hook) {
      if (hook !== null && typeof hook !== 'function') {
        throw new Error('Photo compression hook must be a function or null.');
      }

      photoCompressionHook = hook;
    },

    async createPoi (input = {}, options = {}) {
      const fallbackListId = await resolveDefaultPoiListId();
      const payload = buildCreatePoiRecord(input, fallbackListId, options);

      const id = await GeoMoverDB.addOne('pois', payload);
      const persisted = { ...payload, id };
      this.pois = sortPois([...this.pois, persisted]);
      this.isPoisLoaded = true;
      return persisted;
    },

    async updatePoi (poiId, updates = {}, options = {}) {
      const numericPoiId = Number(poiId);
      if (!Number.isFinite(numericPoiId)) {
        throw new Error('Valid POI ID is required.');
      }

      const current = this.getPoiById(numericPoiId) || await GeoMoverDB.findOne('pois', numericPoiId);
      if (!current) {
        throw new Error('POI not found.');
      }

      const fallbackListId = updates.listId !== undefined
        ? await resolveDefaultPoiListId()
        : null;

      const next = buildUpdatedPoiRecord(current, updates, {
        ...options,
        fallbackListId
      });

      await GeoMoverDB.updateOne('pois', next, numericPoiId);
      this.pois = sortPois([
        ...this.pois.filter((row) => Number(row?.id) !== numericPoiId),
        next
      ]);
      this.isPoisLoaded = true;

      return next;
    },

    async deletePoi (poiId, options = {}) {
      const numericPoiId = Number(poiId);
      if (!Number.isFinite(numericPoiId)) {
        throw new Error('Valid POI ID is required.');
      }

      const existing = this.getPoiById(numericPoiId) || await GeoMoverDB.findOne('pois', numericPoiId);
      if (!existing) return false;

      if (options.deletePhotos !== false) {
        await this.deleteAllPoiPhotos(numericPoiId, { skipPoiUpdate: true });
      }

      if (options.deleteLinks !== false) {
        await this.unlinkAllNotesForPoi(numericPoiId);
        await this.unlinkAllRoutesForPoi(numericPoiId);
      }

      await GeoMoverDB.deleteOne('pois', numericPoiId);
      this.pois = sortPois(this.pois.filter((row) => Number(row?.id) !== numericPoiId));
      return true;
    },

    async setPoiList (poiId, listId, options = {}) {
      return this.updatePoi(poiId, { listId }, {
        versionContext: options.versionContext || 'list:assign',
        bumpVersion: options.bumpVersion !== false
      });
    },

    async setPoiTaxonomies (poiId, taxonomyIds, options = {}) {
      return this.updatePoi(poiId, { taxonomyIds: normalizeIdArray(taxonomyIds) }, {
        versionContext: options.versionContext || 'taxonomy:set',
        bumpVersion: options.bumpVersion !== false
      });
    },

    async addPoiTaxonomy (poiId, taxonomyId, options = {}) {
      const current = this.getPoiById(poiId) || await GeoMoverDB.findOne('pois', Number(poiId));
      if (!current) throw new Error('POI not found.');

      const nextTaxonomyIds = normalizeIdArray([...normalizeIdArray(current.taxonomyIds), taxonomyId]);
      return this.setPoiTaxonomies(poiId, nextTaxonomyIds, {
        versionContext: options.versionContext || 'taxonomy:add',
        bumpVersion: options.bumpVersion !== false
      });
    },

    async removePoiTaxonomy (poiId, taxonomyId, options = {}) {
      const numericTaxonomyId = Number(taxonomyId);
      if (!Number.isFinite(numericTaxonomyId)) throw new Error('Valid taxonomy ID is required.');

      const current = this.getPoiById(poiId) || await GeoMoverDB.findOne('pois', Number(poiId));
      if (!current) throw new Error('POI not found.');

      const nextTaxonomyIds = normalizeIdArray(current.taxonomyIds).filter((id) => id !== numericTaxonomyId);
      return this.setPoiTaxonomies(poiId, nextTaxonomyIds, {
        versionContext: options.versionContext || 'taxonomy:remove',
        bumpVersion: options.bumpVersion !== false
      });
    },

    async setPoiTags (poiId, tagIds, options = {}) {
      return this.updatePoi(poiId, { tagIds: normalizeIdArray(tagIds) }, {
        versionContext: options.versionContext || 'tag:set',
        bumpVersion: options.bumpVersion !== false
      });
    },

    async addPoiTag (poiId, tagId, options = {}) {
      const current = this.getPoiById(poiId) || await GeoMoverDB.findOne('pois', Number(poiId));
      if (!current) throw new Error('POI not found.');

      const nextTagIds = normalizeIdArray([...normalizeIdArray(current.tagIds), tagId]);
      return this.setPoiTags(poiId, nextTagIds, {
        versionContext: options.versionContext || 'tag:add',
        bumpVersion: options.bumpVersion !== false
      });
    },

    async removePoiTag (poiId, tagId, options = {}) {
      const numericTagId = Number(tagId);
      if (!Number.isFinite(numericTagId)) throw new Error('Valid tag ID is required.');

      const current = this.getPoiById(poiId) || await GeoMoverDB.findOne('pois', Number(poiId));
      if (!current) throw new Error('POI not found.');

      const nextTagIds = normalizeIdArray(current.tagIds).filter((id) => id !== numericTagId);
      return this.setPoiTags(poiId, nextTagIds, {
        versionContext: options.versionContext || 'tag:remove',
        bumpVersion: options.bumpVersion !== false
      });
    },

    async linkPoiToNote (poiId, noteId) {
      const numericPoiId = Number(poiId);
      const numericNoteId = Number(noteId);
      if (!Number.isFinite(numericPoiId) || !Number.isFinite(numericNoteId)) {
        throw new Error('Valid POI ID and note ID are required.');
      }

      if (!hasAnyPoiNoteLinksStore()) {
        throw new Error('POI-note link store is unavailable in IndexedDB.');
      }

      if (!this.isPoiNoteLinksLoaded) {
        await this.loadPoiNoteLinks();
      }

      const existing = this.poiNoteLinks.find((row) => (
        Number(row?.poiId) === numericPoiId && Number(row?.noteId) === numericNoteId
      ));
      if (existing) return existing;

      const payload = {
        poiId: numericPoiId,
        noteId: numericNoteId,
        createdAt: Date.now(),
        updatedAt: null
      };

      const id = await GeoMoverDB.addOne(resolvePoiNoteLinksStoreName(), payload);
      const persisted = { ...payload, id };
      this.poiNoteLinks = sortLinks([...this.poiNoteLinks, persisted]);
      this.isPoiNoteLinksLoaded = true;
      return persisted;
    },

    async unlinkPoiFromNote (poiId, noteId) {
      const numericPoiId = Number(poiId);
      const numericNoteId = Number(noteId);
      if (!Number.isFinite(numericPoiId) || !Number.isFinite(numericNoteId)) {
        throw new Error('Valid POI ID and note ID are required.');
      }

      if (!this.isPoiNoteLinksLoaded) {
        await this.loadPoiNoteLinks();
      }

      const existing = this.poiNoteLinks.find((row) => (
        Number(row?.poiId) === numericPoiId && Number(row?.noteId) === numericNoteId
      ));
      if (!existing) return false;

      await GeoMoverDB.deleteOne(resolvePoiNoteLinksStoreName(), Number(existing.id));
      this.poiNoteLinks = sortLinks(this.poiNoteLinks.filter((row) => Number(row?.id) !== Number(existing.id)));
      return true;
    },

    async unlinkAllNotesForPoi (poiId) {
      const numericPoiId = Number(poiId);
      if (!Number.isFinite(numericPoiId)) return 0;

      if (!this.isPoiNoteLinksLoaded) {
        await this.loadPoiNoteLinks();
      }

      if (!hasAnyPoiNoteLinksStore()) return 0;

      const matches = this.poiNoteLinks.filter((row) => Number(row?.poiId) === numericPoiId);
      const linksStore = resolvePoiNoteLinksStoreName();
      for (const row of matches) {
        await GeoMoverDB.deleteOne(linksStore, Number(row.id));
      }

      this.poiNoteLinks = sortLinks(this.poiNoteLinks.filter((row) => Number(row?.poiId) !== numericPoiId));
      return matches.length;
    },

    async unlinkAllPoisForNote (noteId) {
      const numericNoteId = Number(noteId);
      if (!Number.isFinite(numericNoteId)) return 0;

      if (!this.isPoiNoteLinksLoaded) {
        await this.loadPoiNoteLinks();
      }

      if (!hasAnyPoiNoteLinksStore()) return 0;

      const matches = this.poiNoteLinks.filter((row) => Number(row?.noteId) === numericNoteId);
      const linksStore = resolvePoiNoteLinksStoreName();
      for (const row of matches) {
        await GeoMoverDB.deleteOne(linksStore, Number(row.id));
      }

      this.poiNoteLinks = sortLinks(this.poiNoteLinks.filter((row) => Number(row?.noteId) !== numericNoteId));
      return matches.length;
    },

    async linkPoiToRoute (poiId, routeId, options = {}) {
      const numericPoiId = Number(poiId);
      const numericRouteId = Number(routeId);
      if (!Number.isFinite(numericPoiId) || !Number.isFinite(numericRouteId)) {
        throw new Error('Valid POI ID and route ID are required.');
      }

      if (!this.isPoiRouteLinksLoaded) {
        await this.loadPoiRouteLinks();
      }

      const existing = this.poiRouteLinks.find((row) => (
        Number(row?.poiId) === numericPoiId && Number(row?.routeId) === numericRouteId
      ));
      if (existing) return existing;

      const thresholdMeters = normalizeFiniteNumber(options.thresholdMeters, DEFAULT_ROUTE_LINK_THRESHOLD_METERS);
      let validation = null;

      if (typeof options.validateLink === 'function') {
        validation = await options.validateLink({
          poiId: numericPoiId,
          routeId: numericRouteId,
          thresholdMeters
        });
      }
      else {
        const distanceMeters = normalizeFiniteNumber(options.distanceMetersAtLinkTime ?? options.distanceMeters, null);
        if (!Number.isFinite(distanceMeters)) {
          throw new Error('Route link validation is required (provide validateLink callback or distanceMetersAtLinkTime).');
        }

        validation = {
          ok: distanceMeters <= thresholdMeters,
          distanceMeters,
          message: distanceMeters <= thresholdMeters
            ? null
            : `POI is ${Math.round(distanceMeters)}m away from route (threshold ${Math.round(thresholdMeters)}m).`
        };
      }

      if (!validation?.ok) {
        throw new Error(validation?.message || 'POI is outside route-link threshold.');
      }

      const payload = {
        poiId: numericPoiId,
        routeId: numericRouteId,
        distanceMetersAtLinkTime: normalizeFiniteNumber(validation.distanceMeters, null),
        thresholdMeters,
        createdAt: Date.now(),
        updatedAt: null
      };

      const id = await GeoMoverDB.addOne('poiRouteLinks', payload);
      const persisted = { ...payload, id };
      this.poiRouteLinks = sortLinks([...this.poiRouteLinks, persisted]);
      this.isPoiRouteLinksLoaded = true;

      return persisted;
    },

    async unlinkPoiFromRoute (poiId, routeId) {
      const numericPoiId = Number(poiId);
      const numericRouteId = Number(routeId);
      if (!Number.isFinite(numericPoiId) || !Number.isFinite(numericRouteId)) {
        throw new Error('Valid POI ID and route ID are required.');
      }

      if (!this.isPoiRouteLinksLoaded) {
        await this.loadPoiRouteLinks();
      }

      const existing = this.poiRouteLinks.find((row) => (
        Number(row?.poiId) === numericPoiId && Number(row?.routeId) === numericRouteId
      ));
      if (!existing) return false;

      await GeoMoverDB.deleteOne('poiRouteLinks', Number(existing.id));
      this.poiRouteLinks = sortLinks(this.poiRouteLinks.filter((row) => Number(row?.id) !== Number(existing.id)));
      return true;
    },

    async unlinkAllRoutesForPoi (poiId) {
      const numericPoiId = Number(poiId);
      if (!Number.isFinite(numericPoiId)) return 0;

      if (!this.isPoiRouteLinksLoaded) {
        await this.loadPoiRouteLinks();
      }

      const matches = this.poiRouteLinks.filter((row) => Number(row?.poiId) === numericPoiId);
      for (const row of matches) {
        await GeoMoverDB.deleteOne('poiRouteLinks', Number(row.id));
      }

      this.poiRouteLinks = sortLinks(this.poiRouteLinks.filter((row) => Number(row?.poiId) !== numericPoiId));
      return matches.length;
    },

    async unlinkAllPoisForRoute (routeId) {
      const numericRouteId = Number(routeId);
      if (!Number.isFinite(numericRouteId)) return 0;

      if (!this.isPoiRouteLinksLoaded) {
        await this.loadPoiRouteLinks();
      }

      const matches = this.poiRouteLinks.filter((row) => Number(row?.routeId) === numericRouteId);
      for (const row of matches) {
        await GeoMoverDB.deleteOne('poiRouteLinks', Number(row.id));
      }

      this.poiRouteLinks = sortLinks(this.poiRouteLinks.filter((row) => Number(row?.routeId) !== numericRouteId));
      return matches.length;
    },

    async addPoiPhoto (poiId, photoInput, options = {}) {
      const numericPoiId = Number(poiId);
      if (!Number.isFinite(numericPoiId)) {
        throw new Error('Valid POI ID is required.');
      }

      const poi = this.getPoiById(numericPoiId) || await GeoMoverDB.findOne('pois', numericPoiId);
      if (!poi) {
        throw new Error('POI not found.');
      }

      const rawBlob = ensureBlob(photoInput);
      const compressionResult = await getCompressionHook(options.compressionHook)({
        blob: rawBlob,
        poiId: numericPoiId,
        input: photoInput,
        options
      });

      const processedBlob = compressionResult?.blob instanceof Blob
        ? compressionResult.blob
        : rawBlob;

      const now = Date.now();
      const payload = {
        poiId: numericPoiId,
        blob: processedBlob,
        title: normalizeOptionalString(photoInput?.title),
        mimeType: normalizeOptionalString(compressionResult?.mimeType)
          || normalizeOptionalString(photoInput?.mimeType)
          || normalizeOptionalString(processedBlob?.type),
        width: normalizeFiniteNumber(compressionResult?.width ?? photoInput?.width, null),
        height: normalizeFiniteNumber(compressionResult?.height ?? photoInput?.height, null),
        sizeBytes: Number(processedBlob.size || 0),
        source: normalizeOptionalString(photoInput?.source),
        createdAt: now,
        updatedAt: null
      };

      const photoId = await GeoMoverDB.addOne('poiPhotos', payload);
      const persisted = { ...payload, id: photoId };

      this.poiPhotos = sortPhotos([...this.poiPhotos, persisted]);
      this.isPoiPhotosLoaded = true;

      const nextPhotoIds = normalizeIdArray([...normalizeIdArray(poi.photoIds), photoId]);
      const nextAvatarPhotoId = options.setAsAvatar === true || !Number.isFinite(Number(poi.avatarPhotoId))
        ? Number(photoId)
        : Number(poi.avatarPhotoId);

      await this.updatePoi(numericPoiId, {
        photoIds: nextPhotoIds,
        avatarPhotoId: nextAvatarPhotoId
      }, {
        versionContext: options.versionContext || 'photo:add',
        bumpVersion: options.bumpVersion !== false
      });

      return persisted;
    },

    async deletePoiPhoto (poiId, photoId, options = {}) {
      const numericPoiId = Number(poiId);
      const numericPhotoId = Number(photoId);
      if (!Number.isFinite(numericPoiId) || !Number.isFinite(numericPhotoId)) {
        throw new Error('Valid POI ID and photo ID are required.');
      }

      if (!this.isPoiPhotosLoaded) {
        await this.loadPoiPhotos();
      }

      const photo = findById(this.poiPhotos, numericPhotoId) || await GeoMoverDB.findOne('poiPhotos', numericPhotoId);
      if (!photo || Number(photo.poiId) !== numericPoiId) {
        return false;
      }

      const poi = this.getPoiById(numericPoiId) || await GeoMoverDB.findOne('pois', numericPoiId);
      if (!poi) {
        throw new Error('POI not found.');
      }

      await GeoMoverDB.deleteOne('poiPhotos', numericPhotoId);
      this.poiPhotos = sortPhotos(this.poiPhotos.filter((row) => Number(row?.id) !== numericPhotoId));

      if (options.skipPoiUpdate === true) {
        return true;
      }

      const nextPhotoIds = normalizeIdArray(poi.photoIds).filter((id) => id !== numericPhotoId);
      const currentAvatar = normalizeNumericId(poi.avatarPhotoId, null);
      const nextAvatar = currentAvatar === numericPhotoId
        ? (nextPhotoIds[0] ?? null)
        : currentAvatar;

      await this.updatePoi(numericPoiId, {
        photoIds: nextPhotoIds,
        avatarPhotoId: nextAvatar
      }, {
        versionContext: options.versionContext || 'photo:remove',
        bumpVersion: options.bumpVersion !== false
      });

      return true;
    },

    async deleteAllPoiPhotos (poiId, options = {}) {
      const numericPoiId = Number(poiId);
      if (!Number.isFinite(numericPoiId)) return 0;

      if (!this.isPoiPhotosLoaded) {
        await this.loadPoiPhotos();
      }

      const matches = this.poiPhotos.filter((row) => Number(row?.poiId) === numericPoiId);
      for (const row of matches) {
        await GeoMoverDB.deleteOne('poiPhotos', Number(row.id));
      }

      this.poiPhotos = sortPhotos(this.poiPhotos.filter((row) => Number(row?.poiId) !== numericPoiId));

      if (options.skipPoiUpdate !== true) {
        await this.updatePoi(numericPoiId, {
          photoIds: [],
          avatarPhotoId: null
        }, {
          versionContext: options.versionContext || 'photo:clear',
          bumpVersion: options.bumpVersion !== false
        });
      }

      return matches.length;
    },

    async setPoiAvatarPhoto (poiId, photoId, options = {}) {
      const numericPoiId = Number(poiId);
      const numericPhotoId = Number(photoId);
      if (!Number.isFinite(numericPoiId)) throw new Error('Valid POI ID is required.');

      if (!Number.isFinite(numericPhotoId)) {
        return this.updatePoi(numericPoiId, { avatarPhotoId: null }, {
          versionContext: options.versionContext || 'photo:avatar',
          bumpVersion: options.bumpVersion !== false
        });
      }

      const poi = this.getPoiById(numericPoiId) || await GeoMoverDB.findOne('pois', numericPoiId);
      if (!poi) throw new Error('POI not found.');

      const photoIds = normalizeIdArray(poi.photoIds);
      if (!photoIds.includes(numericPhotoId)) {
        throw new Error('Avatar photo must be assigned to the same POI.');
      }

      return this.updatePoi(numericPoiId, { avatarPhotoId: numericPhotoId }, {
        versionContext: options.versionContext || 'photo:avatar',
        bumpVersion: options.bumpVersion !== false
      });
    },

    async getPoiPhotoBlob (photoId) {
      const numericPhotoId = Number(photoId);
      if (!Number.isFinite(numericPhotoId)) return null;

      const photo = findById(this.poiPhotos, numericPhotoId) || await GeoMoverDB.findOne('poiPhotos', numericPhotoId);
      return photo?.blob || null;
    }
  }
});
